import type { MatterField } from '../constants/matterFields';
import type { ProspectMatterType } from '../enums';
import {
    APS_DEPOSIT_HELD_BY_ROLES,
    type ApsClients,
    type ApsDepositHeldByRole,
    type ApsOcrExtraction,
} from '../types/apsOcr';
import { APS_MATTER_FIELDS, getMatterFieldApsKey, getMatterFieldNames } from './matterFields';

export function splitLegalName(fullName: string): { firstName: string; lastName: string } {
    const trimmed = fullName.trim();
    if (!trimmed) {
        return { firstName: '', lastName: '' };
    }

    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
        return { firstName: parts[0], lastName: '' };
    }

    return {
        firstName: parts.slice(0, -1).join(' '),
        lastName: parts[parts.length - 1],
    };
}

function parseNullableCount(value: unknown): number | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'number' && Number.isInteger(value) && value > 0) {
        return value;
    }

    const normalized = String(value).trim();
    if (!normalized) {
        return null;
    }

    const parsed = Number.parseInt(normalized, 10);
    return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
}

function parseNullableString(value: unknown): string | null {
    if (value === null || value === undefined) {
        return null;
    }

    const normalized = String(value).trim();
    return normalized ? normalized : null;
}

function parseStringArray(value: unknown): string[] {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? [trimmed] : [];
    }

    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((entry) => String(entry).trim()).filter(Boolean);
}

function splitCommaSeparatedNames(value: string): string[] {
    if (!/[;,]/.test(value)) {
        return [value];
    }

    return value
        .split(/[;,]/)
        .map((part) => part.trim())
        .filter(Boolean);
}

export function parsePartyNames(value: unknown): string[] {
    return parseStringArray(value).flatMap(splitCommaSeparatedNames);
}

function looksLikeBlankPartyName(value: string): boolean {
    return (
        value.length < 2 ||
        /^[\s./\-_|]+$/.test(value) ||
        /^(buyers?|sellers?|purchasers?|vendors?)$/i.test(value)
    );
}

function captureApsPartyNames(text: string, pattern: RegExp): string[] {
    const match = text.match(pattern);
    const captured = match?.[1]?.trim();
    if (!captured || looksLikeBlankPartyName(captured)) {
        return [];
    }

    return parsePartyNames(captured).filter((name) => !looksLikeBlankPartyName(name));
}

export function extractApsPartyNamesFromText(text: string): {
    purchaserNames: string[];
    sellerNames: string[];
} {
    const normalized = text
        .replace(/\*\*|__/g, '')
        .replace(/[_|]{2,}/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const sellerNames = captureApsPartyNames(
        normalized,
        /SELLERS?\s*,?\s+(.+?)\s*,?\s+the following/i,
    );
    const vendorNames =
        sellerNames.length > 0
            ? []
            : captureApsPartyNames(normalized, /VENDORS?\s*,?\s+(.+?)\s*,?\s+the following/i);

    return {
        purchaserNames: captureApsPartyNames(
            normalized,
            /BUYERS?\s*,?\s+(.+?)\s*,?\s+agrees to purchase/i,
        ),
        sellerNames: sellerNames.length > 0 ? sellerNames : vendorNames,
    };
}

function parseNullableBoolean(value: unknown): boolean | null {
    if (value === null || value === undefined) {
        return null;
    }

    if (typeof value === 'boolean') {
        return value;
    }

    const normalized = String(value).trim().toLowerCase();
    if (!normalized) {
        return null;
    }

    if (normalized === 'true' || normalized === 'yes' || normalized === '1') {
        return true;
    }

    if (normalized === 'false' || normalized === 'no' || normalized === '0') {
        return false;
    }

    return null;
}

function parseDepositHeldByRole(value: unknown): ApsDepositHeldByRole | null {
    const normalized = parseNullableString(value);
    if (!normalized) {
        return null;
    }

    const exact = APS_DEPOSIT_HELD_BY_ROLES.find((role) => role === normalized);
    if (exact) {
        return exact;
    }

    const lower = normalized.toLowerCase();
    return APS_DEPOSIT_HELD_BY_ROLES.find((role) => role.toLowerCase() === lower) ?? null;
}

/**
 * Other names a scan may give the party name lists. Not field aliases, because `buyers`,
 * `purchasers` and `sellers` are legal doc tags that can mean the clients.
 */
const APS_PARTY_NAME_SYNONYMS: Partial<Record<keyof ApsOcrExtraction, string[]>> = {
    purchaserNames: ['buyerNames', 'purchasers', 'buyers'],
    sellerNames: ['vendorNames', 'vendors', 'sellers'],
};

type ApsOcrValue = ApsOcrExtraction[keyof ApsOcrExtraction];

function parseApsValue(field: MatterField, apsKey: keyof ApsOcrExtraction, value: unknown) {
    if (apsKey in APS_PARTY_NAME_SYNONYMS) {
        return parsePartyNames(value);
    }
    if (apsKey === 'depositHeldByRole') {
        return parseDepositHeldByRole(value);
    }
    switch (field.type) {
        case 'yesNo':
            return parseNullableBoolean(value);
        case 'count':
            return parseNullableCount(value);
        case 'list':
            return parseStringArray(value);
        default:
            return parseNullableString(value);
    }
}

function isEmptyApsValue(value: ApsOcrValue): boolean {
    return value === null || (Array.isArray(value) && value.length === 0);
}

/**
 * Every APS field parsed by its type, from its APS key or the first other name of the field
 * (e.g. `completionDate`) that has a value.
 */
export function normalizeApsOcrExtraction(raw: Record<string, unknown>): ApsOcrExtraction {
    const extraction: Partial<Record<keyof ApsOcrExtraction, ApsOcrValue>> = {};
    for (const field of APS_MATTER_FIELDS) {
        const apsKey = getMatterFieldApsKey(field);
        if (!apsKey) {
            continue;
        }
        const names = new Set([
            apsKey,
            ...getMatterFieldNames(field),
            ...(APS_PARTY_NAME_SYNONYMS[apsKey] ?? []),
        ]);
        const values = [...names].map((name) => parseApsValue(field, apsKey, raw[name]));
        extraction[apsKey] = values.find((value) => !isEmptyApsValue(value)) ?? values[0];
    }
    return extraction as ApsOcrExtraction;
}

/** Client rows from the scan: the purchasers on a purchase, the sellers on a sale. */
export function getApsClients(
    extraction: ApsOcrExtraction,
    matterType: ProspectMatterType | '',
): ApsClients {
    const clientNames = matterType === 'Sale' ? extraction.sellerNames : extraction.purchaserNames;
    const parsedClients = clientNames.map((fullName) => {
        const { firstName, lastName } = splitLegalName(fullName);
        return { firstName, lastName, contactEmail: '', isPrimary: false as const };
    });

    const primary =
        parsedClients.length > 0
            ? { ...parsedClients[0], isPrimary: true as const }
            : { firstName: '', lastName: '', contactEmail: '', isPrimary: true as const };

    const additional = parsedClients
        .slice(1)
        .map((client) => ({ ...client, isPrimary: false as const }));

    return { primary, additional };
}

export function parseStoredApsOcrExtraction(value: unknown): ApsOcrExtraction | null {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return normalizeApsOcrExtraction(value as Record<string, unknown>);
}

/** Editable display string for an extraction field. */
export function formatExtractedFieldValue(
    extraction: ApsOcrExtraction,
    key: keyof ApsOcrExtraction,
): string {
    const value = extraction[key];
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
        return value
            .map((entry) => entry.trim())
            .filter(Boolean)
            .join(', ');
    }
    return String(value).trim();
}
